import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';
import { authService } from './authService';
import { logger } from '../utils/logger';

const API_BASE_URL = import.meta.env.VITE_API_URL;

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = useAuthStore.getState().token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        logger.error('[Chat] API error', {
          status: error.response?.status,
          method: originalRequest?.method,
          url: originalRequest?.baseURL && originalRequest?.url ? `${originalRequest.baseURL}${originalRequest.url}` : originalRequest?.url,
          message: error.message,
        });

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const refreshToken = useAuthStore.getState().refreshToken;
          if (refreshToken) {
            logger.warn('[Chat] 401; attempting token refresh');
            const newTokens = await authService.refreshToken(refreshToken);
            if (newTokens) {

              logger.info('[Chat] Token refresh succeeded; retrying request');

              originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
              return this.client(originalRequest);
            }
          }
          logger.error('[Chat] Token refresh failed; logging out');
          useAuthStore.getState().logout();
          // Dispatch custom event to trigger React Router navigation
          window.dispatchEvent(new CustomEvent('auth:logout'));
        }

        if (error.response?.status === 429) {
          const retryAfter = error.response.headers?.['retry-after'];
          logger.warn('[Chat] Rate limited', { retryAfter });
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export const apiService = new ApiService();
